import { useMemo } from 'react';
import { useSelector } from 'react-redux';

import { selectProtocols } from '@state-mgmt/slices/protocol-slice';
import { selectTrials } from '@state-mgmt/slices/trial-slice';
import { selectClientFieldVisits, selectClientProtocolNotes } from '@state-mgmt/slices/visitSlice';

export default function useGetHistoricalVisitTrialsAndProtocols({
  currentTrialId,
  selectedTrialId,
  protocolId
}: {
  selectedTrialId: string | null | undefined;
  currentTrialId: string | null | undefined;
  protocolId: string | null | undefined;
}) {
  const visits = useSelector(selectClientFieldVisits);
  const notes = useSelector(selectClientProtocolNotes)
  const stateTrials = useSelector(selectTrials);
  const stateProtocols = useSelector(selectProtocols);

  const trials = useMemo(() => {
    const all: {
      id: string;
      name: string;
      year: number;
      defaultProtocolId: string | null;
    }[] = [];

    const currentTrial = stateTrials?.find(t => t.id === currentTrialId);
    if (!!currentTrial) {
      all.push({
        id: currentTrial.id,
        name: currentTrial.name,
        year: currentTrial.year,
        defaultProtocolId: protocolId || null
      });
    }

    visits?.forEach(v => {
      if (!all.some(existing => v.trial_id === existing.id)) {
        all.push({
          id: v.trial_id,
          name: v.trial_name,
          year: v.trial_year,
          defaultProtocolId: v.protocol_id
        });
      }
    });

    notes?.forEach(n => {
      if (!all.some(existing => n.trial_id === existing.id)) {
        all.push({
          id: n.trial_id,
          name: n.trial_name,
          year: n.trial_year,
          defaultProtocolId: n.protocol_id
        });
      }
    });



    return all.sort((a, b) => {
      if (b.year !== a.year) {
        return b.year - a.year;
      }

      return a.name.localeCompare(b.name);
    });
  }, [visits, currentTrialId, stateTrials, protocolId, notes]);

  const protocols = useMemo(() => {
    const all: {
      id: string;
      name: string;
    }[] = [];

    if (!selectedTrialId) return all;

    visits?.forEach(v => {
      if (v.trial_id === selectedTrialId && !all.some(existing => v.protocol_id === existing.id)) {
        all.push({
          id: v.protocol_id,
          name: v.protocol_name
        });
      }
    });

    notes?.forEach(n => {
      if (n.trial_id === selectedTrialId && !all.some(existing => n.protocol_id === existing.id)) {
        all.push({
          id: n.protocol_id,
          name: n.protocol_name
        });
      }
    });

    if (selectedTrialId === currentTrialId && protocolId && stateProtocols && !all.some(p => p.id === protocolId)) {
      const stateProtocol = stateProtocols?.find(p => p.id === protocolId);
      if (stateProtocol) {
        all.push({
          id: stateProtocol.id,
          name: stateProtocol.name
        });
      }
    }

    return all.sort((a, b) => a.name.localeCompare(b.name));
  }, [visits, protocolId, selectedTrialId, stateProtocols, notes]);

  return {
    trials,
    protocols
  };
}
